import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CircleCheck,
  CirclePause,
  Copy,
  FileBarChart,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import useListParams, { usePageParam } from '../../components/useListParams'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import DateRangeField from '../../components/DateRangeField'
import ExportMenu from '../../components/ExportMenu'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import CampaignFormDrawer from './CampaignFormDrawer'
import {
  campaignState,
  copyCampaign,
  deleteCampaign,
  getCampaignSummary,
  getCampaignsPage,
  listCampaigns,
  saveCampaign,
  setCampaignActive,
} from './phishingService'
import { campaignsStrings as S } from './strings'
import './phishing.css'
import { serverText } from '../../lib/i18n'

/**
 * حملات الإصطياد — Views/PhishingCampaigns/Index.cshtml.
 *
 * The five outcome columns are a funnel: every user counted at one stage was also
 * counted at the ones before it, so the numbers only ever fall left to right.
 * `تم التبليغ` is the good outcome and gets the success tone; `ادخل بيانات` is the
 * worst and gets the danger tone.
 */
/** The four derived states, in the order a campaign passes through them. */
const STATE_TONE = { scheduled: 'info', running: 'primary', finished: 'success', inactive: 'dark' }

/**
 * THE DATE RANGE IS AN ADDRESS, not a working position: it moves every figure on the
 * screen, so `?from=2026-01-01&to=2026-03-31` IS the quarter's funnel and can be sent to
 * someone else or reloaded onto the same numbers. Empty means all time and stays out of
 * the URL. Module-level, as `useListParams` requires.
 */
const RANGE_PARAMS = { from: '', to: '' }

export default function CampaignsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [query, setQuery] = useRemembered('phishing-campaigns:query', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one — without this the table renders its "no
     records" state for a server error, which is the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [rangeParams, setRange] = useListParams(RANGE_PARAMS)
  /* Rebuilt from the two strings rather than used as it comes: `useListParams` hands back
     a fresh object whenever ANY parameter changes, the page included, and the summary
     below is fetched off this identity — so paging would refetch the funnel. */
  const range = useMemo(
    () => ({ from: rangeParams.from, to: rangeParams.to }),
    [rangeParams.from, rangeParams.to],
  )
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingToggle, setPendingToggle] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [options, setOptions] = useState({})
  const [summary, setSummary] = useState(null)
  /* In the address, so Back and a refresh both land on the same page. */
  const [page, setPage] = usePageParam()
  /* How many rows the reader chose to see — not an address, but it was lost on every
     visit to a campaign, so a list set to 100 came back at 25. */
  const [pageSize, setPageSize] = useRemembered('phishing-campaigns:pageSize', 25)
  const [total, setTotal] = useState(0)
  const [saving, setSaving] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const openForm = (campaign) => {
    setEditing(campaign ?? null)
    setFormOpen(true)
  }

  /* The lookups the form chooses from, and the summary the count columns come from. Loaded
     once — none of it changes while the list is paged. */
  useEffect(() => {
    let cancelled = false
    getCampaignsPage()
      .then((data) => {
        if (cancelled) return
        const { summary: loaded, ...lists } = data
        setOptions(lists)
        setSummary(loaded)
      })
      .catch((error) => !cancelled && toast.error(error?.message || S.actionFailed))
    return () => {
      cancelled = true
    }
  }, [reloadKey, toast])

  /* A new search is a new list, so it starts at its first page — on a CHANGE only, or the
     page carried in the address would be thrown away on arrival. The table cannot do this
     itself: the list is SERVER-paged, so the page belongs to this screen. */
  useOnChange(() => setPage(1), [query])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listCampaigns({ page, pageSize, query })
      .then((response) => {
        if (cancelled) return
        setLoadError(null)
        setRows(response.rows)
        setTotal(response.total)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setTotal(0)
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [page, pageSize, query, reloadKey, toast])

  /* THE DATE RANGE NARROWS THE SUMMARY, not the list. `/summary` is the only route that takes
     one — `/filter` has no date parameter at all — so the range moves the counts and the
     totals, which is what a range over a funnel means. */
  useEffect(() => {
    if (!range.from && !range.to) return undefined
    let cancelled = false
    getCampaignSummary(range)
      .then((loaded) => !cancelled && setSummary(loaded))
      .catch((error) => !cancelled && toast.error(error?.message || S.actionFailed))
    return () => {
      cancelled = true
    }
  }, [range, toast])

  /* The row carries no counts — the campaign DTO has none — so they are merged in from the
     summary by id, the way the groups list merges its member counts. */
  const visibleRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        ...(summary?.byCampaign?.get(row.id) ?? {
          sent: null,
          opened: null,
          clicked: null,
          submitted: null,
          reported: null,
        }),
      })),
    [rows, summary],
  )

  /* Memoised because the column definitions close over it. */
  const run = useCallback(
    async (action, fallback) => {
      const outcome = await action
      if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
      toast.success(serverText(outcome.message) || fallback)
      setReloadKey((key) => key + 1)
      return undefined
    },
    [toast],
  )

  const save = async (values) => {
    setSaving(true)
    const outcome = await saveCampaign({ ...values, id: editing?.id ?? 0 })
    setSaving(false)
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || S.saved)
    setFormOpen(false)
    setReloadKey((key) => key + 1)
    return undefined
  }

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: S.columns.name,
        width: '260px',
        render: (row) => (
          <span className="ph__campaign">
            <bdi className="ph__campaign-name">{row.name}</bdi>
            {row.template && <bdi className="ph__campaign-template">{row.template}</bdi>}
          </span>
        ),
      },
      {
        key: 'startDate',
        header: S.columns.period,
        value: (row) => row.startDate ?? '',
        /* Each date is one unbreakable run; only the pair may split. Left to the
           browser the line broke inside `2026-07-23`, at its own hyphens. */
        render: (row) => (
          <span className="ph__period" dir="ltr">
            {/* The dash rides with the start date, so a wrap gives two lines
                rather than three with a lone dash between them. */}
            <span className="ph__date">{row.startDate ?? ''} —</span>{' '}
            <span className="ph__date">{row.endDate ?? ''}</span>
          </span>
        ),
      },
      { key: 'sent', header: S.columns.sent, align: 'center' },
      { key: 'opened', header: S.columns.opened, align: 'center' },
      { key: 'clicked', header: S.columns.clicked, align: 'center' },
      {
        key: 'submitted',
        header: S.columns.submitted,
        align: 'center',
        /* Nobody fell for it is the BEST outcome on this column, and a red 0
           reads as an alarm over exactly that. A zero carries no signal either
           way here, so it steps back instead of wearing a status colour. */
        render: (row) => (
          <span className={row.submitted > 0 ? 'ph__bad' : 'ph__nil'}>{row.submitted}</span>
        ),
      },
      {
        key: 'reported',
        header: S.columns.reported,
        align: 'center',
        render: (row) => (
          <span className={row.reported > 0 ? 'ph__good' : 'ph__nil'}>{row.reported}</span>
        ),
      },
      {
        key: 'isActive',
        header: S.columns.status,
        /* FOUR STATES, DERIVED. A campaign has no status column — not on the DTO and not in
           the database — so it is read from the three facts it does carry: whether it is
           active at all, whether it has actually started, and whether it has finished. */
        value: (row) => S.states[campaignState(row)],
        render: (row) => {
          const state = campaignState(row)
          return <StatusBadge tone={STATE_TONE[state]}>{S.states[state]}</StatusBadge>
        },
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        /* Six glyphs held this column open at 230px, which was most of the 337px
           that pushed the list into horizontal scroll. The report and the edit
           lead; the other four keep their icons and gain a visible label. */
        render: (row) => (
          <RowActions
            actions={[
              /* The report screen exists now. `التنبيهات` has no endpoint anywhere in this
                 slice, so it is REMOVED rather than left declared — a row action that
                 opens nothing is the thing this sweep exists to take out. COSMETIC_UI.md. */
              {
                id: 'report',
                icon: FileBarChart,
                label: S.row.report,
                tone: 'info',
                onClick: () => navigate(`/phishing/report/${row.id}`),
              },
              { id: 'edit', icon: Pencil, label: S.edit, onClick: () => openForm(row) },
              {
                id: 'active',
                /* `Ban` — the crossed circle — is the mark for FORBIDDEN, and stopping a
                   campaign is not that; it is the same pause every other list in the panel
                   offers, and it is what the status chip on this row already shows. */
                icon: row.isActive ? CirclePause : CircleCheck,
                label: row.isActive ? S.row.disable : S.row.activate,
                tone: row.isActive ? 'warning' : 'success',
                onClick: () => setPendingToggle(row),
              },
              {
                id: 'copy',
                icon: Copy,
                label: S.row.copy,
                tone: 'primary',
                onClick: () => run(copyCampaign(row.id), S.copied),
              },
              {
                id: 'delete',
                icon: Trash2,
                label: S.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [navigate, run],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.editor}
        count={loading ? undefined : total}
        actions={
          <>
            <Button onClick={() => openForm(null)}>
              <Plus size={16} strokeWidth={2} />
              {S.addNew}
            </Button>
          </>
        }
      />

      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: S.columns.name }}
        chips={
          range.from || range.to
            ? [
                {
                  id: 'range',
                  label: S.report,
                  value: `${range.from || '…'} — ${range.to || '…'}`,
                  onRemove: () => setRange({ from: '', to: '' }),
                },
              ]
            : []
        }
        /* No export endpoint exists for the campaigns list — the phishing exports live on
           the report slice, per campaign. Declared in COSMETIC_UI.md. */
        actions={<ExportMenu onExport={() => {}} labels={{ excel: S.report, pdf: 'PDF' }} />}
      >
        <DateRangeField
          value={range}
          onChange={setRange}
          labels={{ from: S.from, to: S.to }}
        />
      </FilterBar>

      <DataTable
        error={loadError}
        columns={columns}
        rows={visibleRows}
        loading={loading}
        query={query}
        getRowId={(row) => row.id}
        server={{
          page,
          pageSize,
          total,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
          },
        }}
      />

      <CampaignFormDrawer
        open={formOpen}
        campaign={editing}
        {...options}
        onClose={() => setFormOpen(false)}
        saving={saving}
        onSave={save}
      />

      {/* `استيراد` IS GONE — see `COSMETIC_UI.md`, "Phishing campaigns — import".
          There is no campaign import endpoint: the modal took a file, sent nothing, and
          raised the DELETE confirmation as its success message. Its template link
          (`/root/ExcelTemplates/Phishing.xlsx`) 404s as well — the file has never existed. */}

      <ConfirmDialog
        open={!!pendingDelete}
        message={`${S.deleteConfirm} ${S.deleteWarning}`}
        confirmLabel={S.delete}
        cancelLabel={S.close}
        onConfirm={() => {
          const campaign = pendingDelete
          setPendingDelete(null)
          run(deleteCampaign(campaign.id), S.deleted)
        }}
        onCancel={() => setPendingDelete(null)}
      />

      {/* DELETING A CAMPAIGN DESTROYS ITS REPORT. The server removes its departments, its
          users and every recorded event with no guard for one that has already run, so a
          finished campaign's results go with it — hence a confirm that says so. */}
      <ConfirmDialog
        open={!!pendingToggle}
        title={pendingToggle?.isActive ? S.disableTitle : S.activateTitle}
        tone={pendingToggle?.isActive ? 'warning' : 'success'}
        message={pendingToggle?.isActive ? S.disableConfirm : S.activateConfirm}
        confirmLabel={pendingToggle?.isActive ? S.row.disable : S.row.activate}
        cancelLabel={S.close}
        onConfirm={() => {
          const campaign = pendingToggle
          setPendingToggle(null)
          run(setCampaignActive(campaign.id, !campaign.isActive), S.saved)
        }}
        onCancel={() => setPendingToggle(null)}
      />
    </>
  )
}
